import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Pair } from '@api/core';
import { BasePairIconPipe } from '@shared/pipes/base-pair-icon.pipe';
import { matchText } from '@shared/utils/strings';
import { AssetIconComponent } from '@stillmandigital/assets-icons';
import { isString } from 'lodash';
import { AutocompleteSelectorComponent } from '../autocomplete-selector/autocomplete-selector.component';

@Component({
  selector: 'app-pair-selector',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatPrefix,
    MatSuffix,
    MatLabel,
    MatIcon,
    AssetIconComponent,
    MatAutocomplete,
    MatOption,
    MatIconButton,
    MatInput,
    AsyncPipe,
    MatAutocompleteTrigger,
    BasePairIconPipe,
  ],
  templateUrl: './pair-selector.component.html',
  styleUrl: './pair-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PairSelectorComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PairSelectorComponent),
      multi: true,
    },
  ],
})
export class PairSelectorComponent<T extends Pair> extends AutocompleteSelectorComponent<T> {
  public matchOption(pair: T, value: string): boolean {
    return matchText(this.displayOption(pair), value);
  }

  public displayWith = input<((pair: T | undefined) => string) | undefined>();

  public displayOption = (pair: string | T | undefined): string => {
    if (isString(pair)) {
      return pair;
    }
    const customDisplay = this.displayWith();
    if (customDisplay && pair) {
      return customDisplay(pair);
    }
    return pair?.name ?? '';
  };

  public extractId(option: T | undefined): string | number | undefined {
    return option?.id;
  }

  public label = input<string>('Pair');
}
